import { Alert, Button, Modal, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { QrCode2FA } from "../modalAutenticador/qrCode2FA";

const { Text } = Typography;

const SEGUNDOS_VISIVEL = 60;

/** Modal de QR Code de credencial: HelpSystem e Tri7 usam as mesmas regras.
 *
 * Um QR Code de OTP e a propria seed -- quem fotografa a tela gera os codigos
 * para sempre. Por isso nada e buscado ao abrir: o usuario precisa pedir, o
 * QR Code some sozinho e a otpauth_url sai do estado junto.
 */
export const ModalQrCodeProtegido = ({
  visible,
  onCancel,
  titulo,
  avisoMensagem,
  avisoDescricao,
  buscarQrCode,
  mensagemErro,
}) => {
  const [otpAuthUrl, setOtpAuthUrl] = useState("");
  const [login, setLogin] = useState("");
  const [erro, setErro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const intervaloRef = useRef(null);

  const revelado = Boolean(otpAuthUrl);

  const pararContagem = useCallback(() => {
    if (intervaloRef.current) {
      clearInterval(intervaloRef.current);
      intervaloRef.current = null;
    }
  }, []);

  const esconderQrCode = useCallback(() => {
    pararContagem();
    setOtpAuthUrl("");
    setSegundosRestantes(0);
  }, [pararContagem]);

  const exibirQrCode = async () => {
    setErro(null);
    setLoading(true);

    try {
      const response = await buscarQrCode();

      if (response.status === 200) {
        setOtpAuthUrl(response.data.otpauth_url);
        setLogin(response.data.login || "");
        setSegundosRestantes(SEGUNDOS_VISIVEL);
      }
    } catch (error) {
      const dados = error.response?.data;
      setErro(dados?.detail || dados?.token || mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!revelado || intervaloRef.current) {
      return;
    }

    intervaloRef.current = setInterval(() => {
      setSegundosRestantes((anterior) => anterior - 1);
    }, 1000);

    return pararContagem;
  }, [revelado, pararContagem]);

  useEffect(() => {
    if (revelado && segundosRestantes <= 0) {
      esconderQrCode();
    }
  }, [revelado, segundosRestantes, esconderQrCode]);

  useEffect(() => {
    if (!visible) {
      esconderQrCode();
      setErro(null);
      setLogin("");
    }
  }, [visible, esconderQrCode]);

  useEffect(() => esconderQrCode, [esconderQrCode]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={titulo}
      footer={null}
      width={500}
      bodyStyle={{ padding: 20 }}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={avisoMensagem}
        description={avisoDescricao}
      />

      {erro && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={typeof erro === "string" ? erro : "Opa, ocorreu um erro!"}
        />
      )}

      {revelado ? (
        <>
          <QrCode2FA otpAuthUrl={otpAuthUrl} loading={false} />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 12,
            }}
          >
            {login ? (
              <Text style={{ color: "#5c6f85" }}>
                Conta: <strong>{login}</strong>
              </Text>
            ) : (
              <span />
            )}

            <Text style={{ color: "#5c6f85" }}>
              Some em <strong>{segundosRestantes}s</strong>
            </Text>
          </div>
        </>
      ) : (
        <div
          style={{
            border: "1px dashed #d6e5f7",
            borderRadius: 14,
            padding: 32,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Text style={{ color: "#5c6f85", textAlign: "center" }}>
            O QR Code fica visível por {SEGUNDOS_VISIVEL} segundos.
          </Text>

          <Button
            type="primary"
            icon={<EyeOutlined />}
            loading={loading}
            onClick={exibirQrCode}
          >
            Exibir QR Code
          </Button>
        </div>
      )}
    </Modal>
  );
};
